import { useI18n } from '@change-corgi/core/react/i18n';

import { usePromotePageData, usePromotePageState } from 'src/app/pages/promote/pageContext';
import { useActions, useTracking } from 'src/app/pages/promote/shared/hooks';
import { calculateImpressions } from 'src/app/shared/utils/promote';

type Result = ModelHookResult<
	{
		currencyCode: string;
		baseImpressionCount: number;
		impressionCount: number;
		isSelected: boolean;
		isBonusCpm: boolean;
		isDouble: boolean;
	},
	Pick<ReturnType<typeof useActions>, 'setAmount'>
>;

export function useImpressionAmountButton(buttonAmount: number): Result {
	const { amountToBaseUnits } = useI18n();

	const {
		data: {
			promotionSettings: { cpm, bonusCpm, bonusThreshold },
			promotionCountrySettings: { amountChoices },
			currency: { code },
		},
	} = usePromotePageData();
	const [{ amount }] = usePromotePageState();
	const { setAmount } = useActions();

	const { trackAmountChanged } = useTracking();

	const isSelected = amount === buttonAmount;
	const amountInBaseUnits = amountToBaseUnits(buttonAmount, code);
	const isBonusCpm = bonusCpm && bonusThreshold ? amountInBaseUnits >= bonusThreshold : false;
	const baseImpressionCount = calculateImpressions(amountInBaseUnits, cpm);
	const bonusImpressionCount = calculateImpressions(amountInBaseUnits, cpm, bonusCpm, bonusThreshold);
	const isDouble = isBonusCpm && bonusCpm ? cpm / bonusCpm === 2 : false;
	const buttonIndex = amountChoices.indexOf(buttonAmount) + 1;

	const onAmountChanged = (a: number) => {
		setAmount(a);
		trackAmountChanged({ buttonWasClicked: true, buttonClicked: `p${buttonIndex}` });
	};

	return {
		data: {
			currencyCode: code,
			baseImpressionCount,
			impressionCount: isBonusCpm ? bonusImpressionCount : baseImpressionCount,
			isSelected,
			isBonusCpm,
			isDouble,
		},
		actions: {
			setAmount: onAmountChanged,
		},
	};
}
